import React from "react";
import { StyleSheet, View } from "react-native";
import EtiquetteCard from "./EtiquetteCard";
import EarIcon from "../../assets/EarIcon";
import ShieldIcon from "../../assets/ShieldIcon";
import HeartHandshakeIcon from "../../assets/HeartHandshakeIcon";
import PlantIcon from "../../assets/PlantIcon";
import LockIcon from "../../assets/LockIcon";
import BulbIcon from "../../assets/BulbIcon";

const etiquetteRules = [
  {
    title: "Respect boundaries",
    description:
      "Everyone brings their own comfort level — and that always comes first.",
    icon: <ShieldIcon width={44} height={44} />,
    expandedContent: [
      "• Ask for consent before changing topics or going deeper into sensitive subjects.",
      "• Pay attention to emotional signals and respond with curiosity, not defensiveness.",
      "• It's okay to pause, slow down, or redirect a conversation at any time.",
    ],
  },
  {
    title: "Listen with presence",
    description: "Let others finish. Acknowledge before responding.",
    icon: <EarIcon width={44} height={44} />,
    expandedContent: [
      "• Show that you're engaged — through attentive posture, gentle expressions, and presence.",
      "• Allow others to finish their thoughts before responding.",
      "• Acknowledge what the other person shared before adding your own perspective.",
    ],
  },
  {
    title: "Communicate with care",
    description: "Tone and body language shape how conversations feel.",
    icon: <HeartHandshakeIcon width={44} height={44} />,
    expandedContent: [
      "• Use a calm, friendly, and respectful tone.",
      "• Keep your body language open and welcoming.",
      "• Avoid gestures, expressions, or reactions that could feel dismissive, aggressive, or judgmental.",
    ],
  },
  {
    title: "Kindness over performance",
    description: "This is a practice space. Awkward is okay.",
    icon: <PlantIcon width={44} height={44} />,
    expandedContent: [
      "• Speak genuinely and authentically; avoid sarcasm or language that could alienate others.",
      "• Use affirming words and encourage when it feels appropriate.",
      "• Feeling awkward is okay. Learning together is the point. Be kind to yourself and to others.",
    ],
  },
  {
    title: "Trust & safety",
    description: "No recording. No pressure. You can leave or report anytime.",
    icon: <LockIcon width={44} height={44} />,
    expandedContent: [
      "• Never record, photograph, or share interactions without clear permission.",
      "• Using the platform to promote personal businesses or services without authorization is not allowed and may result in restricted access.",
    ],
  },
  {
    title: "Feedback with intention",
    description: "Offer feedback only when invited.",
    icon: <BulbIcon width={44} height={44} />,
    expandedContent: [
      "• Offer feedback only when invited — and always with care and respect.",
      "• Receive feedback with openness; it's an opportunity to grow, not a judgment.",
      "• Avoid unsolicited advice or critique.",
    ],
  },
];

type EtiquetteRulesProps = {
  variant?: "desktop" | "mobile";
};

export default function EtiquetteRules({
  variant = "desktop",
}: EtiquetteRulesProps) {
  const isMobile = variant === "mobile";
  return (
    <View style={[styles.wrapper, !isMobile && styles.wrapperDesktop]}>
      <View
        style={[
          styles.gridContainer,
          !isMobile && styles.gridContainerDesktop,
          isMobile && styles.gridContainerMobile,
        ]}
      >
        {etiquetteRules.map((rule) => (
          <EtiquetteCard
            key={rule.title}
            title={rule.title}
            description={rule.description}
            icon={rule.icon}
            expandedContent={rule.expandedContent}
            variant={variant}
          />
        ))}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  wrapper: {
    width: "100%",
    alignSelf: "center",
  },
  wrapperDesktop: {
    width: 1293,
    height: 572,
  },
  gridContainer: {
    width: "100%",
    alignItems: "center",
  },
  gridContainerDesktop: {
    flexDirection: "row",
    flexWrap: "wrap",
    justifyContent: "flex-start",
    alignItems: "flex-start",
    paddingHorizontal: 8,
    gap: 24,
  },
  gridContainerMobile: {
    flexDirection: "column",
    gap: 16,
  },
});
